import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaRegPauseCircle, FaRegPlayCircle } from 'react-icons/fa'; // Import icons
import recurrenceOptions from '../../data/recurrenceOptions';
import statusOptions from '../../data/status';
import dewormingTypes from '../../data/dewormingTypes';
import dewormingMethods from '../../data/dewormingMethods';
import '../../style/DewormingDetailsPage.css';
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n instance

const DewormingDetailsPage = () => {
    const { id, dewormingId } = useParams(); // Get pet ID and deworming ID from URL
    const navigate = useNavigate();
    const { t } = useTranslation(); // Translation function
    const [deworming, setDeworming] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDateInput, setShowDateInput] = useState(false); // State to toggle date input visibility
    const [givenDate, setGivenDate] = useState(''); // State to store the selected date
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchDewormingDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('deworming.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                setDeworming(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchDewormingDetails();
    }, [id, dewormingId, baseUrl, t]);

    const getRecurrenceLabel = (value) => {
        const option = recurrenceOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const getStatusLabel = (value) => {
        const option = statusOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const getTypeLabel = (value) => {
        const option = dewormingTypes.find((type) => type.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const getMethodLabel = (value) => {
        const option = dewormingMethods.find((method) => method.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const handleToggleStatus = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const newStatus = deworming.status === 'active' ? 'pause' : 'activate';
            const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: newStatus }),
            });

            if (!response.ok) {
                throw new Error(t('deworming.updateFailedStatus')); // Use translation for error
            }

            const updatedDeworming = await response.json();
            setDeworming(updatedDeworming.find((v) => v._id === dewormingId)); // Update the deworming state
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsGiven = () => {
        setShowDateInput(true); // Show the date input
    };

    const handleConfirmGivenDate = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings/${dewormingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: 'given', date: givenDate }), // Send the selected date
            });

            if (!response.ok) {
                throw new Error(t('deworming.markGivenFailed')); // Use translation for error
            }

            const updatedDeworming = await response.json();
            setDeworming(updatedDeworming.find((v) => v._id === dewormingId)); // Update the deworming state
            setShowDateInput(false); // Hide the date input
            setGivenDate(''); // Reset the date input
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        navigate(`/pet/${id}/deworming/${dewormingId}/edit`);
    };

    const handleCancelDateInput = () => {
        setShowDateInput(false); // Hide the date input
        setGivenDate(''); // Reset the date input
    };

    if (error) {
        return <div className="error-message text-red-500 text-center">{error}</div>;
    }

    if (!deworming) {
        return <div className="loading-message text-dafauna-gray text-center">{t('general.loading')}</div>;
    }

    return (
        <div className="deworming-details-page bg-dafauna-background rounded-xl shadow-md p-8">
            <div className="deworming-header flex justify-between items-center mb-4">
                <div className="header-left flex items-center gap-4">
                    <h2 className="text-dafauna-darkText text-2xl font-bold">{getMethodLabel(deworming.method)}</h2>
                    <FaEdit
                        className="edit-icon text-dafauna-primary hover:text-dafauna-primaryLight cursor-pointer"
                        onClick={handleEditClick}
                        title={t('deworming.titleEdit')}
                    />
                </div>
                <div className="header-right">
                    <div
                        onClick={handleToggleStatus}
                        className="status-icon cursor-pointer"
                        title={deworming.status === 'active' ? t('deworming.pause') : t('deworming.activate')}
                    >
                        {deworming.status === 'active' ? (
                            <FaRegPauseCircle className="pause-icon text-red-500 text-xl" />
                        ) : (
                            <FaRegPlayCircle className="play-icon text-dafauna-primary text-xl" />
                        )}
                    </div>
                </div>
            </div>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.status')}:</strong> {getStatusLabel(deworming.status) || t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.type')}:</strong> {getTypeLabel(deworming.type) || t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.lastOne')}:</strong> {deworming.lastGivenDate ? new Date(deworming.lastGivenDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.nextOne')}:</strong> {deworming.nextGivenDate ? new Date(deworming.nextGivenDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.end')}:</strong> {deworming.endDate ? new Date(deworming.endDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.recurrence')}:</strong> {getRecurrenceLabel(deworming.recurrence) || t('general.na')}</p>

            <div className="actions mt-4">
                {deworming.status === 'active' && (
                    <>
                        <button
                            onClick={handleMarkAsGiven}
                            className="mark-as-given-btn"
                            disabled={loading}
                        >
                            {loading ? t('general.updating') : t('general.markAsGiven')}
                        </button>
                        {showDateInput && (
                            <div className="date-input-container flex flex-col gap-4 mt-4">
                                <h4 className="text-dafauna-darkText">{t('general.confirmGiven')}</h4>
                                <input
                                    type="date"
                                    value={givenDate}
                                    onChange={(e) => setGivenDate(e.target.value)}
                                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    required
                                />
                                <div className="date-input-actions flex gap-4">
                                    <button
                                        onClick={handleConfirmGivenDate}
                                        className="confirm-date-btn"
                                        disabled={loading || !givenDate}
                                    >
                                        {t('general.confirm')}
                                    </button>
                                    <button
                                        onClick={handleCancelDateInput}
                                        className="cancel-date-btn"
                                        disabled={loading}
                                    >
                                        {t('general.cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <h3 className="text-dafauna-darkText text-xl font-bold mt-4">{t('general.history')}</h3>
            {deworming.history && deworming.history.length > 0 ? (
                <table className="history-table w-full border-collapse mt-4">
                    <thead>
                        <tr>
                            <th className="bg-dafauna-gray text-white">{t('general.date')}</th>
                            <th className="bg-dafauna-primary text-white">{t('general.action')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {deworming.history
                            .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date in descending order
                            .map((entry, index) => (
                                <tr key={index}>
                                    <td className="border border-gray-300 p-2">{new Date(entry.date).toLocaleDateString()}</td>
                                    <td className="border border-gray-300 p-2">{entry.notes || t('general.noNotes')}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            ) : (
                <p className="text-dafauna-gray">{t('deworming.noHistory')}</p>
            )}
        </div>
    );
};

export default DewormingDetailsPage;